import { MapPin, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const CANADIAN_PROVINCES = [
  ['QC', 'Quebec'], ['ON', 'Ontario'], ['BC', 'British Columbia'],
  ['AB', 'Alberta'], ['MB', 'Manitoba'], ['SK', 'Saskatchewan'],
  ['NS', 'Nova Scotia'], ['NB', 'New Brunswick'],
  ['NL', 'Newfoundland and Labrador'], ['PE', 'Prince Edward Island'],
  ['NT', 'Northwest Territories'], ['YT', 'Yukon'], ['NU', 'Nunavut'],
];

export const OnboardingStepLocations = ({
  locations, companyInfo, addLocation, removeLocation, updateLocation,
}) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <MapPin className="h-5 w-5 text-[#7B1F2A]" />
        <h3 className="text-lg font-semibold">Company Locations</h3>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addLocation}
        className="text-[#7B1F2A]"
      >
        <Plus className="h-4 w-4 mr-1" />
        Add Location
      </Button>
    </div>

    <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
      {locations.map((location, index) => (
        <div
          key={`${location.name || 'loc'}-${index}`}
          className={`p-4 rounded-lg border ${location.is_primary ? 'border-[#7B1F2A] bg-blue-50/50' : 'border-slate-200'}`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-900">
                {companyInfo.company_name || 'Company'} - {location.name || `Location ${index + 1}`}
              </span>
              {location.is_primary && (
                <span className="text-xs bg-[#7B1F2A] text-white px-2 py-0.5 rounded">Primary</span>
              )}
            </div>
            {locations.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeLocation(index)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Location Name *</Label>
              <Input
                value={location.name}
                onChange={(e) => updateLocation(index, 'name', e.target.value)}
                placeholder="Montreal HQ"
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input
                value={location.phone}
                onChange={(e) => updateLocation(index, 'phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
                className="h-9"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Address</Label>
              <Input
                value={location.address}
                onChange={(e) => updateLocation(index, 'address', e.target.value)}
                placeholder="123 Main Street"
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">City</Label>
              <Input
                value={location.city}
                onChange={(e) => updateLocation(index, 'city', e.target.value)}
                placeholder="Kahnawà:ke"
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Province</Label>
              <Select
                value={location.province}
                onValueChange={(v) => updateLocation(index, 'province', v)}
              >
                <SelectTrigger className="h-9"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent className="bg-white">
                  {CANADIAN_PROVINCES.map(([code, name]) => (
                    <SelectItem key={code} value={code}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Postal Code</Label>
              <Input
                value={location.postal_code}
                onChange={(e) => updateLocation(index, 'postal_code', e.target.value)}
                placeholder="H2X 1Y4"
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input
                type="email"
                value={location.email}
                onChange={(e) => updateLocation(index, 'email', e.target.value)}
                placeholder="montreal@company.com"
                className="h-9"
              />
            </div>
            {!location.is_primary && (
              <div className="md:col-span-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => updateLocation(index, 'is_primary', true)}
                  className="text-xs"
                >
                  Set as Primary Location
                </Button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);
